import {
  AreaColumnUnit,
  CsvImportOperationResult,
  CsvImportPreviewResponse,
  LeadImportAvBucket,
  useLeads,
  useOperation,
  useOrganisationList,
} from "@inrange/building-manager-api-client";
import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Container,
  Form,
  Modal,
} from "react-bootstrap";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import LeadsHelpModal from "./LeadsHelpModal";

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        reject(new Error("Failed to read file as data URL"));
        return;
      }
      resolve(result.split(",", 2)[1] ?? "");
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const parseTags = (raw: string): string[] =>
  Array.from(
    new Set(
      raw
        .split("\n")
        .map((t) => t.trim())
        .filter(Boolean)
    )
  );

// Portfolio tags need to be URL-safe (they become path segments at
// /leads/portfolio/<tag>). Lowercase + replace whitespace/punctuation with
// '-'; trim leading/trailing dashes. Empty result means "operator hasn't
// typed a portfolio tag yet" - treat as no portfolio.
const slugifyPortfolioTag = (raw: string): string =>
  raw
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/^-+|-+$/g, "");

type PortfolioRole = "tenant" | "landlord";

// Per-import outcome breakdown, keyed by Google's `validationGranularity`.
// The buckets + `incompleteCount` are computed BE-side (the operation
// result is an aggregate summary, not the created leads) and mirror the
// CSV-import gate logic - see `_summarise_created` in
// operation_lead_csv_import.py. The labels below stay aligned with what
// the right-panel's `AddressVerdictRow` renders per lead
// (`humanizeGranularity` in addressVerdict.ts).
type OutcomeBucket = LeadImportAvBucket;

// Operator-facing labels - the underlying enum is Google's PREMISE /
// SUB_PREMISE / ROUTE / OTHER but we don't make the operator learn it.
const BUCKET_LABEL: Record<OutcomeBucket, string> = {
  PREMISE: "Located the building",
  SUB_PREMISE: "Located a unit within the building",
  ROUTE: "Street-level only (no specific building)",
  OTHER: "Couldn't recognise the address",
  NO_VERDICT: "No address validation",
};
const BUCKET_COLOUR: Record<OutcomeBucket, string> = {
  PREMISE: "success",
  SUB_PREMISE: "success",
  ROUTE: "warning",
  OTHER: "danger",
  NO_VERDICT: "secondary",
};

// Operator-facing label for an area column key the BE couldn't unit-resolve.
// The BE returns its normalised key (`building_area_gia`); show it closer to
// how it appeared in the sheet.
const humanizeColumnKey = (key: string): string =>
  key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// Operator-facing labels for the canonical keys a source column can be
// mapped onto. Keys not listed fall back to the humanized key.
const TARGET_LABEL: Record<string, string> = {
  site_name_or_id: "Site name / Site ID",
  full_address: "Full address",
  postcode: "Postcode",
  occupier_name: "Occupier name",
  building_area_sqm: "Building area (sqm)",
  building_area_sqft: "Building area (sqft)",
  number_of_buildings: "Number of buildings",
  annual_kwh: "Annual electricity (kWh)",
  building_use: "Building use",
  hh_data_filename: "Half hourly demand data filename",
};

const AREA_UNIT_OPTIONS: { value: AreaColumnUnit; label: string }[] = [
  { value: "sqm", label: "Square metres" },
  { value: "sqft", label: "Square feet" },
  { value: "ignore", label: "Don't use it" },
];

const formatEtaSeconds = (totalSeconds: number): string => {
  if (totalSeconds < 60) return `${Math.max(1, Math.round(totalSeconds))}s`;
  const mins = Math.ceil(totalSeconds / 60);
  return `${mins} min`;
};

const LeadImport: React.FC = () => {
  document.title = "Import leads";
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { importLeadsFromCsv } = useLeads();

  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [tagsRaw, setTagsRaw] = useState("");
  const [helpOpen, setHelpOpen] = useState(false);

  // Portfolio section state. The portfolio tag is a single value (unlike
  // the additional-tags input, which is comma-separated): it becomes a
  // path segment at /leads/portfolio/<tag> + the key for
  // lead_portfolio_metadata, both of which need a stable single string.
  // The default org (optional) is picked from the existing org list, and
  // the operator declares whether that org acts as the leads' landlord
  // or tenant. The BE accepts both `defaultTenantOrgId` and
  // `defaultLandlordOrgId` - we send whichever matches the picked role.
  const [portfolioTagRaw, setPortfolioTagRaw] = useState("");
  const [portfolioDisplayName, setPortfolioDisplayName] = useState("");
  const [defaultOrgId, setDefaultOrgId] = useState("");
  const [defaultOrgRole, setDefaultOrgRole] = useState<PortfolioRole>("tenant");
  const [orgQuery, setOrgQuery] = useState("");

  const orgListQuery = useOrganisationList();
  const orgOptions = useMemo(() => {
    const all = orgListQuery.fetchOrganisations.data?.organisations ?? [];
    return [...all].sort((a, b) =>
      (a.name ?? "").localeCompare(b.name ?? "")
    );
  }, [orgListQuery.fetchOrganisations.data]);
  const filteredOrgs = useMemo(() => {
    const q = orgQuery.trim().toLowerCase();
    if (!q) return orgOptions.slice(0, 50);
    return orgOptions
      .filter((o) => (o.name ?? "").toLowerCase().includes(q))
      .slice(0, 50);
  }, [orgOptions, orgQuery]);
  const selectedOrg = orgOptions.find((o) => o.id === defaultOrgId) ?? null;

  // The in-flight import operation we're polling. Cleared on
  // "Import another file". Once the operation reaches a terminal
  // status the polling stops and we read its `result` for the
  // bucket breakdown.
  const [operationHandle, setOperationHandle] = useState<{
    operationKey: string;
    siteId: string;
    rowCount: number;
  } | null>(null);

  // Preview from the first BE call (no confirmEnrichment). When set,
  // the FE shows a cost-estimate modal; the operator confirms to run
  // the actual import or cancels to back out. Mirrors /leads/search.
  const [pendingPreview, setPendingPreview] = useState<
    CsvImportPreviewResponse & { fileContentBase64: string; sourceKind: "csv" | "xlsx" }
    | null
  >(null);

  // Operator's answers for area columns the BE couldn't unit-resolve, keyed
  // by column key. Starts empty on every preview so nothing is pre-answered:
  // an unanswered column blocks confirm rather than silently defaulting.
  const [areaColumnUnits, setAreaColumnUnits] = useState<
    Record<string, AreaColumnUnit>
  >({});

  // Operator-declared column mapping, stored target -> source (how the
  // operator thinks: "which column is the address?"). Inverted to
  // source -> target when submitted, which is the shape the BE applies.
  const [columnMapByTarget, setColumnMapByTarget] = useState<
    Record<string, string>
  >({});

  const ambiguousAreaColumns =
    pendingPreview?.unitAmbiguousAreaColumns ?? [];
  const unansweredAreaColumns = ambiguousAreaColumns.filter(
    (c) => !areaColumnUnits[c.key]
  );
  const missingRequiredColumns = pendingPreview?.missingRequiredColumns ?? [];
  const availableColumns = pendingPreview?.availableColumns ?? [];
  const mappableTargets = pendingPreview?.mappableTargets ?? [];

  // What the BE actually resolved, per target: the column now carrying that
  // target's data is the one whose key IS the target key (auto-matching
  // renames it), so this reads back the detected mapping rather than
  // asserting one. Empty string = nothing detected for that target.
  const detectedSourceForTarget = (target: string): string =>
    availableColumns.some((c) => c.key === target) ? target : "";

  // Selections start as whatever was detected, so the dropdowns SHOW the plan
  // instead of an opaque "leave as detected".
  const selectedSourceForTarget = (target: string): string =>
    columnMapByTarget[target] ?? detectedSourceForTarget(target);

  const mappingChanged = mappableTargets.some(
    (t) => selectedSourceForTarget(t) !== detectedSourceForTarget(t)
  );
  const applyMappingDisabled = importLeadsFromCsv.isLoading || !mappingChanged;
  const runImportDisabled =
    importLeadsFromCsv.isLoading ||
    unansweredAreaColumns.length > 0 ||
    missingRequiredColumns.length > 0;

  // Only the operator's overrides are submitted. Re-sending a detected
  // mapping would be a no-op that reads as an override in the payload.
  const columnMapToSubmit = Object.fromEntries(
    mappableTargets
      .filter(
        (t) =>
          !!selectedSourceForTarget(t) &&
          selectedSourceForTarget(t) !== detectedSourceForTarget(t)
      )
      .map((t) => [selectedSourceForTarget(t), t])
  );

  const additionalTags = parseTags(tagsRaw);
  const portfolioTag = slugifyPortfolioTag(portfolioTagRaw);

  // The portfolio tag is auto-added to the tags list at submit time so
  // every imported lead inherits it. Render it as a non-removable chip
  // so the operator sees it'll be applied.
  const tagsToSubmit = portfolioTag
    ? Array.from(new Set([portfolioTag, ...additionalTags]))
    : additionalTags;

  // Validation: a default org with no portfolio tag can't be persisted
  // (the lead_portfolio_metadata row is keyed on the tag). Show a guard
  // alert + disable submit in that combination.
  const portfolioConfigInvalid = !!defaultOrgId && !portfolioTag;

  // Poll the operation while we have a handle. `useOperation` stops
  // polling automatically once status is terminal.
  const operationPoll = useOperation(
    operationHandle?.operationKey ?? null,
    operationHandle?.siteId ?? null
  );
  const operation = operationPoll.data?.operation ?? null;
  const operationStatus = operation?.status ?? null;
  const operationProgress = operation?.progress ?? null;
  const importResult =
    operationStatus === "completed" && operation?.result
      ? (operation.result as unknown as CsvImportOperationResult)
      : null;

  // Refresh the leads cache when the import finishes. The kick-off
  // mutation's onSuccess fires when the operation is queued, not when
  // it completes, so the new leads aren't visible on /leads otherwise
  // until the next page navigation. useOperation stops polling once
  // it reaches a terminal status so this effect fires at most once.
  useEffect(() => {
    if (operationStatus === "completed") {
      queryClient.invalidateQueries(["leads"]);
    }
  }, [operationStatus, queryClient]);

  const kickingOff = importLeadsFromCsv.isLoading;
  const pollingActive =
    !!operationHandle &&
    operationStatus !== "completed" &&
    operationStatus !== "failed" &&
    operationStatus !== "cancelled";
  const busy = kickingOff || pollingActive || !!pendingPreview;
  const disabled = busy || !csvFile || portfolioConfigInvalid;

  const buildImportRequest = (
    fileContentBase64: string,
    sourceKind: "csv" | "xlsx",
    confirmEnrichment: boolean
  ) => ({
    sourceKind,
    sourceFilename: csvFile?.name ?? "",
    fileContentBase64,
    tags: tagsToSubmit.length ? tagsToSubmit : undefined,
    portfolioTag: portfolioTag || undefined,
    portfolioDisplayName: portfolioDisplayName.trim() || undefined,
    defaultTenantOrgId:
      defaultOrgRole === "tenant" && defaultOrgId ? defaultOrgId : undefined,
    defaultLandlordOrgId:
      defaultOrgRole === "landlord" && defaultOrgId ? defaultOrgId : undefined,
    confirmEnrichment,
    // Only meaningful on the confirm call - the preview is what tells us
    // which columns need an answer in the first place.
    areaColumnUnits: confirmEnrichment ? areaColumnUnits : undefined,
    // Sent on BOTH calls: the mapping changes what parses, so the preview
    // has to be re-run with it to show whether it actually resolved.
    columnMap: Object.keys(columnMapToSubmit).length
      ? columnMapToSubmit
      : undefined,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!csvFile) return;
    if (portfolioConfigInvalid) return;
    const fileContentBase64 = await fileToBase64(csvFile);
    const sourceKind = csvFile.name.toLowerCase().endsWith(".xlsx")
      ? "xlsx"
      : "csv";
    setOperationHandle(null);
    setPendingPreview(null);
    setAreaColumnUnits({});
    setColumnMapByTarget({});
    try {
      // Step 1 of the two-step preview: send without confirmEnrichment.
      // BE parses the file + returns a cost estimate; we show a modal so
      // the operator can cancel before we pay for the per-row geocoder /
      // AV / Places calls.
      const response = await importLeadsFromCsv.mutateAsync(
        buildImportRequest(fileContentBase64, sourceKind, false)
      );
      if (response.status === "preview") {
        setPendingPreview({
          ...response,
          fileContentBase64,
          sourceKind,
        });
      } else {
        // BE skipped the preview (e.g. zero rows after filter) - fall
        // through to the pending-operation polling path.
        setOperationHandle({
          operationKey: response.operationKey,
          siteId: response.siteId,
          rowCount: response.rowCount,
        });
      }
    } catch {
      // Surfaced via importLeadsFromCsv.isError below.
    }
  };

  // Re-run the preview with the operator's mapping applied. Cheap (no
  // external API calls fire until confirm) and it's the only way to show
  // whether the mapping actually resolved the required columns.
  const handleApplyMapping = async () => {
    if (!pendingPreview) return;
    const { fileContentBase64, sourceKind } = pendingPreview;
    try {
      const response = await importLeadsFromCsv.mutateAsync(
        buildImportRequest(fileContentBase64, sourceKind, false)
      );
      if (response.status === "preview") {
        setPendingPreview({ ...response, fileContentBase64, sourceKind });
        // A remap can change which area columns are ambiguous, so previous
        // unit answers may no longer apply to what's now on screen.
        setAreaColumnUnits({});
      }
    } catch {
      // Surfaced via importLeadsFromCsv.isError below.
    }
  };

  const handleConfirmImport = async () => {
    if (!pendingPreview) return;
    const { fileContentBase64, sourceKind } = pendingPreview;
    setPendingPreview(null);
    try {
      const response = await importLeadsFromCsv.mutateAsync(
        buildImportRequest(fileContentBase64, sourceKind, true)
      );
      if (response.status === "pending") {
        setOperationHandle({
          operationKey: response.operationKey,
          siteId: response.siteId,
          rowCount: response.rowCount,
        });
      }
    } catch {
      // Surfaced via importLeadsFromCsv.isError below.
    }
  };

  const handleImportAnother = () => {
    setOperationHandle(null);
    setPendingPreview(null);
    importLeadsFromCsv.reset();
    setCsvFile(null);
    setTagsRaw("");
    setPortfolioTagRaw("");
    setPortfolioDisplayName("");
    setDefaultOrgId("");
    setOrgQuery("");
    setDefaultOrgRole("tenant");
    setAreaColumnUnits({});
    setColumnMapByTarget({});
  };

  return (
    <Container fluid style={{ padding: 16, maxWidth: 720 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <h3 style={{ margin: 0 }}>Import leads</h3>
        <Button
          variant="link"
          style={{ padding: 0, fontSize: 14 }}
          onClick={() => setHelpOpen(true)}
        >
          How does this work?
        </Button>
      </div>
      <p style={{ color: "#666", fontSize: 13 }}>
        Upload a CSV or XLSX of properties. Each row is enriched with
        building polygon, HMLR proprietors, and Companies House
        candidates, then lands as a Lead on the /leads list ready for
        review. The import runs as a background operation so large
        files don&apos;t hit the API timeout; if the import runs out
        of time before finishing every row the BE reports how many it
        managed so you can re-upload the tail.
      </p>
      <Card>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group>
              <Form.Label>File (.csv or .xlsx)</Form.Label>
              <Form.Control
                type="file"
                accept=".csv,.xlsx"
                disabled={busy}
                onChange={(e) =>
                  setCsvFile(
                    (e.target as HTMLInputElement).files?.[0] ?? null
                  )
                }
              />
            </Form.Group>
            <fieldset
              style={{
                marginTop: 16,
                border: "1px solid #ddd",
                borderRadius: 6,
                padding: 12,
              }}
            >
              <legend
                style={{
                  width: "auto",
                  padding: "0 6px",
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#444",
                }}
              >
                Portfolio (optional)
              </legend>
              <Form.Text muted style={{ fontSize: 11, display: "block", marginBottom: 8 }}>
                Set this when uploading a single customer&apos;s RFI (e.g.
                HSBC&apos;s portfolio) so every lead inherits the same
                portfolio tag and the default landlord/tenant org gets
                applied at bulk-create-sites time.
              </Form.Text>

              <Form.Group>
                <Form.Label style={{ fontSize: 13 }}>Portfolio tag</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g. hsbc-q2-2026"
                  value={portfolioTagRaw}
                  disabled={busy}
                  onChange={(e) => setPortfolioTagRaw(e.target.value)}
                />
                <Form.Text muted style={{ fontSize: 11 }}>
                  Single value. Becomes the URL segment at
                  /leads/portfolio/&lt;tag&gt;. Lowercased + dashed
                  automatically:{" "}
                  {portfolioTag ? (
                    <Badge bg="primary" style={{ fontWeight: 400 }}>
                      {portfolioTag}
                    </Badge>
                  ) : (
                    <em>(none)</em>
                  )}
                </Form.Text>
              </Form.Group>

              <Form.Group style={{ marginTop: 10 }}>
                <Form.Label style={{ fontSize: 13 }}>
                  Display name <em style={{ color: "#888" }}>(optional)</em>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g. HSBC Q2 2026 RFI"
                  value={portfolioDisplayName}
                  disabled={busy}
                  onChange={(e) => setPortfolioDisplayName(e.target.value)}
                />
              </Form.Group>

              <Form.Group style={{ marginTop: 10 }}>
                <Form.Label style={{ fontSize: 13 }}>
                  Default org <em style={{ color: "#888" }}>(optional)</em>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Type to search existing orgs…"
                  value={selectedOrg ? selectedOrg.name : orgQuery}
                  disabled={busy}
                  onChange={(e) => {
                    setOrgQuery(e.target.value);
                    if (defaultOrgId) setDefaultOrgId("");
                  }}
                />
                {!selectedOrg && orgQuery && filteredOrgs.length > 0 && (
                  <div
                    style={{
                      border: "1px solid #ddd",
                      borderTop: "none",
                      maxHeight: 200,
                      overflowY: "auto",
                      background: "white",
                    }}
                  >
                    {filteredOrgs.map((o) => (
                      <div
                        key={o.id}
                        onClick={() => {
                          setDefaultOrgId(o.id);
                          setOrgQuery("");
                        }}
                        style={{
                          padding: "6px 10px",
                          cursor: "pointer",
                          borderBottom: "1px solid #f0f0f0",
                          fontSize: 13,
                        }}
                        onMouseDown={(e) => e.preventDefault()}
                      >
                        {o.name}{" "}
                        <span style={{ color: "#888", fontSize: 11 }}>
                          {o.orgType}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {selectedOrg && (
                  <Button
                    variant="link"
                    size="sm"
                    style={{ padding: 0, fontSize: 12, marginTop: 4 }}
                    disabled={busy}
                    onClick={() => {
                      setDefaultOrgId("");
                      setOrgQuery("");
                    }}
                  >
                    Clear selection
                  </Button>
                )}
              </Form.Group>

              <Form.Group style={{ marginTop: 10 }}>
                <Form.Label style={{ fontSize: 13 }}>
                  Default org role
                </Form.Label>
                <div>
                  <Form.Check
                    inline
                    type="radio"
                    label="Tenant"
                    name="defaultOrgRole"
                    id="defaultOrgRole-tenant"
                    checked={defaultOrgRole === "tenant"}
                    disabled={busy || !defaultOrgId}
                    onChange={() => setDefaultOrgRole("tenant")}
                  />
                  <Form.Check
                    inline
                    type="radio"
                    label="Landlord"
                    name="defaultOrgRole"
                    id="defaultOrgRole-landlord"
                    checked={defaultOrgRole === "landlord"}
                    disabled={busy || !defaultOrgId}
                    onChange={() => setDefaultOrgRole("landlord")}
                  />
                </div>
                <Form.Text muted style={{ fontSize: 11 }}>
                  Applied at bulk-create-sites time as the fallback for
                  any lead whose own {defaultOrgRole === "tenant" ? "tenant" : "landlord"}
                  {" "}decision is unset. Per-lead enrichment matches still
                  override.
                </Form.Text>
              </Form.Group>

              {portfolioConfigInvalid && (
                <Alert variant="warning" className="mt-2 py-1 small mb-0">
                  Default org is set but the portfolio tag is empty. Add a
                  portfolio tag to persist the default org, or clear the
                  org selection.
                </Alert>
              )}
            </fieldset>

            <Form.Group style={{ marginTop: 16 }}>
              <Form.Label>Additional tags (optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="e.g. BS11, q2-2026"
                value={tagsRaw}
                disabled={busy}
                onChange={(e) => setTagsRaw(e.target.value)}
              />
              <Form.Text muted style={{ fontSize: 11 }}>
                Comma- or newline-separated. Applied alongside the
                portfolio tag (if set) so you can filter on /leads.
              </Form.Text>
              {tagsToSubmit.length > 0 && (
                <div style={{ marginTop: 6 }}>
                  {tagsToSubmit.map((t) => (
                    <Badge
                      key={t}
                      bg={t === portfolioTag ? "primary" : "secondary"}
                      style={{ marginRight: 4, fontWeight: 400 }}
                    >
                      {t}
                    </Badge>
                  ))}
                </div>
              )}
            </Form.Group>
            <Button
              type="submit"
              disabled={disabled}
              style={{ marginTop: 12 }}
            >
              {kickingOff
                ? "Starting..."
                : pollingActive
                  ? "Importing..."
                  : "Import"}
            </Button>
            {importLeadsFromCsv.isError && (
              <Alert variant="danger" style={{ marginTop: 8 }}>
                {(importLeadsFromCsv.error as Error)?.message ??
                  "Failed to start the import."}
              </Alert>
            )}
            {operationHandle && pollingActive && (
              <div style={{ marginTop: 12, fontSize: 13 }}>
                <p style={{ marginBottom: 6 }}>
                  Importing <strong>{operationHandle.rowCount}</strong> row
                  {operationHandle.rowCount === 1 ? "" : "s"}. Estimated
                  time:{" "}
                  <strong>
                    {/* ~4s/row sequential, /5 worker threads = ~0.8s/row
                       effective. Rough ceiling shown to set expectations. */}
                    {formatEtaSeconds(
                      Math.max(15, operationHandle.rowCount * 0.8)
                    )}
                  </strong>{" "}
                  (each row hits 4-5 external lookups).
                </p>
                {operationProgress && operationProgress.total > 0 && (
                  <>
                    <p style={{ marginBottom: 4, color: "#555" }}>
                      {operationProgress.message} ({operationProgress.current}/
                      {operationProgress.total})
                    </p>
                    <div
                      style={{
                        background: "#e9ecef",
                        borderRadius: 4,
                        height: 8,
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          background: "#198754",
                          width: `${
                            (100 * operationProgress.current) /
                            operationProgress.total
                          }%`,
                          height: "100%",
                          transition: "width 200ms ease",
                        }}
                      />
                    </div>
                  </>
                )}
                <p style={{ fontSize: 11, color: "#999", marginTop: 8 }}>
                  It&apos;s safe to leave this page - the operation keeps
                  running on the BE and the new leads will appear on /leads
                  once complete.
                </p>
              </div>
            )}
            {operationHandle &&
              (operationStatus === "failed" ||
                operationStatus === "cancelled") && (
                <Alert variant="danger" style={{ marginTop: 8 }}>
                  The import {operationStatus}:{" "}
                  {operation?.error ?? "see CloudWatch logs for details"}.
                </Alert>
              )}
            {importResult && (() => {
              const createdCount = importResult.createdCount;
              const buckets = importResult.avBuckets;
              const incompleteCount = importResult.incompleteCount;
              const orderedBuckets: OutcomeBucket[] = [
                "PREMISE",
                "SUB_PREMISE",
                "ROUTE",
                "OTHER",
                "NO_VERDICT",
              ];
              const partial = importResult.rowsSkipped > 0;
              return (
                <Alert
                  variant={partial ? "warning" : "success"}
                  style={{ marginTop: 8 }}
                >
                  <div style={{ marginBottom: 6, fontWeight: 500 }}>
                    Imported {createdCount} lead
                    {createdCount === 1 ? "" : "s"}
                    {importResult.errorCount > 0 && (
                      <>
                        {" "}
                        ({importResult.errorCount} row
                        {importResult.errorCount === 1 ? "" : "s"} failed -
                        see logs)
                      </>
                    )}
                    .
                  </div>
                  {importResult.rowsAlreadyImported > 0 && (
                    <div
                      style={{
                        marginBottom: 8,
                        fontSize: 13,
                        color: "#664d03",
                      }}
                    >
                      <strong>{importResult.rowsAlreadyImported}</strong> row
                      {importResult.rowsAlreadyImported === 1 ? "" : "s"} were
                      skipped because this file had already been imported.
                      Nothing was duplicated.
                    </div>
                  )}
                  {importResult.errors.length > 0 && (
                    <div
                      style={{
                        marginBottom: 8,
                        fontSize: 12,
                        color: "#664d03",
                      }}
                    >
                      First failure: {importResult.errors[0].error}
                      {importResult.errorCount >
                        importResult.errors.length && (
                        <>
                          {" "}
                          (showing {importResult.errors.length} of{" "}
                          {importResult.errorCount})
                        </>
                      )}
                    </div>
                  )}
                  {partial && (
                    <div
                      style={{
                        marginBottom: 8,
                        fontSize: 13,
                        color: "#664d03",
                      }}
                    >
                      The import ran out of time after attempting{" "}
                      <strong>{importResult.rowsAttempted}</strong> of{" "}
                      <strong>{importResult.rowsTotal}</strong> rows.{" "}
                      <strong>{importResult.rowsSkipped}</strong> row
                      {importResult.rowsSkipped === 1 ? "" : "s"} at the
                      tail of the file weren&apos;t processed - re-upload
                      rows starting at row{" "}
                      <strong>{importResult.rowsAttempted + 1}</strong> to
                      finish.
                    </div>
                  )}
                  <div style={{ fontSize: 12 }}>
                    Address Validation breakdown:
                    <div style={{ marginTop: 4 }}>
                      {orderedBuckets
                        .filter((b) => buckets[b] > 0)
                        .map((b) => (
                          <Badge
                            key={b}
                            bg={BUCKET_COLOUR[b]}
                            style={{ marginRight: 6, fontWeight: 400 }}
                          >
                            {buckets[b]} {BUCKET_LABEL[b]}
                          </Badge>
                        ))}
                      {incompleteCount > 0 && (
                        <Badge
                          bg="warning"
                          text="dark"
                          style={{ marginRight: 6, fontWeight: 400 }}
                        >
                          {incompleteCount} flagged incomplete
                        </Badge>
                      )}
                    </div>
                    {buckets.OTHER > 0 && (
                      <div style={{ marginTop: 6, color: "#666" }}>
                        OTHER rows skipped downstream enrichment - operator
                        should fix the address (or delete the row) on /leads.
                      </div>
                    )}
                    {buckets.ROUTE > 0 && (
                      <div style={{ marginTop: 4, color: "#666" }}>
                        ROUTE rows were enriched but only matched at road
                        level - verify the building polygon picked.
                      </div>
                    )}
                    {incompleteCount > 0 && (
                      <div style={{ marginTop: 4, color: "#666" }}>
                        Incomplete rows still ran the full enrichment - the
                        right panel surfaces Google&apos;s
                        addressComplete=false signal so you can review which
                        components it flagged.
                      </div>
                    )}
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() => navigate("/leads")}
                    >
                      Go to /leads
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      style={{ marginLeft: 8 }}
                      onClick={handleImportAnother}
                    >
                      Import another file
                    </Button>
                  </div>
                </Alert>
              );
            })()}
          </Form>
        </Card.Body>
      </Card>
      <LeadsHelpModal
        show={helpOpen}
        variant="import"
        onHide={() => setHelpOpen(false)}
      />
      <Modal
        show={pendingPreview !== null}
        onHide={() => setPendingPreview(null)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm import</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {pendingPreview && (
            <>
              <p>
                This import will enrich{" "}
                <strong>
                  {(
                    pendingPreview.rowCount -
                    pendingPreview.alreadyImportedCount
                  ).toLocaleString()}
                </strong>{" "}
                row
                {pendingPreview.rowCount -
                  pendingPreview.alreadyImportedCount ===
                1
                  ? ""
                  : "s"}{" "}
                through Google Address Validation, Mapbox, Google Places,
                Companies House, OSM and HMLR.
              </p>
              {pendingPreview.alreadyImportedCount > 0 && (
                <Alert variant="warning" style={{ padding: "8px 12px" }}>
                  <strong>
                    {pendingPreview.alreadyImportedCount.toLocaleString()}
                  </strong>{" "}
                  of {pendingPreview.rowCount.toLocaleString()} row
                  {pendingPreview.rowCount === 1 ? "" : "s"} in this file
                  {pendingPreview.alreadyImportedCount === 1
                    ? " has"
                    : " have"}{" "}
                  already been imported and will be skipped - a lead exists
                  for {pendingPreview.alreadyImportedCount === 1 ? "it" : "them"}{" "}
                  already. Re-importing is safe: nothing is duplicated, and
                  you are not charged for the skipped rows.
                  {pendingPreview.alreadyImportedCount ===
                    pendingPreview.rowCount && (
                    <>
                      {" "}
                      <strong>
                        Every row is already imported, so this run would do
                        nothing.
                      </strong>{" "}
                      To change tags on the existing leads, edit them on
                      /leads instead.
                    </>
                  )}
                </Alert>
              )}
              <p>
                Estimated cost:{" "}
                <strong>
                  ${pendingPreview.estimatedCostUsd.toFixed(2)}
                </strong>{" "}
                <span style={{ color: "#666" }}>
                  (~${pendingPreview.costPerRowUsd.toFixed(3)}/row, paid
                  APIs only - HMLR, CH and OSM are free)
                </span>
                .
              </p>
              <div
                style={{
                  border:
                    missingRequiredColumns.length > 0
                      ? "1px solid #f5c2c7"
                      : "1px solid #ddd",
                  background:
                    missingRequiredColumns.length > 0 ? "#fff5f5" : "#fafafa",
                  borderRadius: 6,
                  padding: 12,
                  marginBottom: 12,
                }}
              >
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>
                  Column mapping
                </div>
                <Form.Text
                  muted
                  style={{ fontSize: 11, display: "block", marginBottom: 10 }}
                >
                  {missingRequiredColumns.length > 0 ? (
                    <>
                      We couldn&rsquo;t work out which of your columns{" "}
                      <strong>
                        {missingRequiredColumns
                          .map((t) => TARGET_LABEL[t] ?? humanizeColumnKey(t))
                          .join(" and ")}
                      </strong>{" "}
                      {missingRequiredColumns.length === 1 ? "is" : "are"}.
                      Choose {missingRequiredColumns.length === 1 ? "it" : "them"}{" "}
                      below and the import can go ahead.
                    </>
                  ) : (
                    <>
                      This is how we read your file. Change anything we got
                      wrong, then apply.
                    </>
                  )}
                </Form.Text>
                {mappableTargets.map((target) => {
                  const selected = selectedSourceForTarget(target);
                  const isRequiredAndMissing =
                    missingRequiredColumns.includes(target);
                  return (
                    <div
                      key={target}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginBottom: 6,
                      }}
                    >
                      <div style={{ fontSize: 13, minWidth: 170 }}>
                        {TARGET_LABEL[target] ?? humanizeColumnKey(target)}
                        {isRequiredAndMissing && (
                          <span style={{ color: "#b02a37" }}> *</span>
                        )}
                      </div>
                      <Form.Select
                        size="sm"
                        value={selected}
                        isInvalid={isRequiredAndMissing && !selected}
                        onChange={(e) =>
                          setColumnMapByTarget((prev) => ({
                            ...prev,
                            [target]: e.target.value,
                          }))
                        }
                      >
                        <option value="">
                          {isRequiredAndMissing
                            ? "— choose a column —"
                            : "— not used —"}
                        </option>
                        {availableColumns.map((col) => (
                          <option key={col.key} value={col.key}>
                            {col.label}
                            {col.sampleValues.length > 0
                              ? ` (${col.sampleValues.join(", ")})`
                              : ""}
                          </option>
                        ))}
                      </Form.Select>
                    </div>
                  );
                })}
                {/* An outline button at Bootstrap's disabled opacity (.65) is
                    still mostly blue on white, so it reads as clickable. Swap
                    to a filled grey when it isn't, which is unmistakable at a
                    glance rather than a subtle fade. */}
                <Button
                  size="sm"
                  variant={applyMappingDisabled ? "secondary" : "outline-primary"}
                  style={
                    applyMappingDisabled
                      ? {
                          marginTop: 4,
                          background: "#e9ecef",
                          borderColor: "#dee2e6",
                          color: "#adb5bd",
                          opacity: 1,
                          cursor: "not-allowed",
                        }
                      : { marginTop: 4 }
                  }
                  disabled={applyMappingDisabled}
                  title={
                    !mappingChanged
                      ? "Change a column above to apply a different mapping"
                      : undefined
                  }
                  onClick={handleApplyMapping}
                >
                  {importLeadsFromCsv.isLoading ? "Applying…" : "Apply mapping"}
                </Button>
              </div>
              {ambiguousAreaColumns.length > 0 && (
                <div
                  style={{
                    border: "1px solid #ffe69c",
                    background: "#fff9e6",
                    borderRadius: 6,
                    padding: 12,
                    marginBottom: 12,
                  }}
                >
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: 13,
                      marginBottom: 6,
                    }}
                  >
                    What unit {ambiguousAreaColumns.length === 1 ? "is" : "are"}{" "}
                    {ambiguousAreaColumns.length === 1
                      ? "this column"
                      : "these columns"}{" "}
                    in?
                  </div>
                  <Form.Text
                    muted
                    style={{ fontSize: 11, display: "block", marginBottom: 10 }}
                  >
                    The header doesn&rsquo;t say, and square metres and square
                    feet differ by 10.8x - too close to a genuine multi-storey
                    building for us to tell them apart afterwards. Building
                    area drives which polygon we match, so we&rsquo;d rather
                    ask than guess.
                  </Form.Text>
                  {ambiguousAreaColumns.map((col) => (
                    <div key={col.key} style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 13 }}>
                        <strong>{humanizeColumnKey(col.key)}</strong>{" "}
                        <span style={{ color: "#888", fontSize: 11 }}>
                          ({col.rowsPopulated} row
                          {col.rowsPopulated === 1 ? "" : "s"}
                          {col.sampleValues.length > 0 && (
                            <>, e.g. {col.sampleValues.join(", ")}</>
                          )}
                          )
                        </span>
                      </div>
                      <div style={{ marginTop: 2 }}>
                        {AREA_UNIT_OPTIONS.map((opt) => (
                          <Form.Check
                            key={opt.value}
                            inline
                            type="radio"
                            label={opt.label}
                            name={`areaUnit-${col.key}`}
                            id={`areaUnit-${col.key}-${opt.value}`}
                            checked={areaColumnUnits[col.key] === opt.value}
                            onChange={() =>
                              setAreaColumnUnits((prev) => ({
                                ...prev,
                                [col.key]: opt.value,
                              }))
                            }
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <p style={{ fontSize: 13, color: "#666" }}>
                Once running, this can&rsquo;t be cancelled - the worker
                drains in-flight rows before stopping. Cancel here to
                back out before any external API calls fire.
              </p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setPendingPreview(null)}
          >
            Cancel
          </Button>
          <Button
            variant={runImportDisabled ? "secondary" : "primary"}
            onClick={handleConfirmImport}
            style={
              runImportDisabled
                ? {
                    background: "#e9ecef",
                    borderColor: "#dee2e6",
                    color: "#adb5bd",
                    opacity: 1,
                    cursor: "not-allowed",
                  }
                : undefined
            }
            disabled={runImportDisabled}
            title={
              missingRequiredColumns.length > 0
                ? "Map the missing required columns first"
                : unansweredAreaColumns.length > 0
                  ? "Choose a unit for every area column first"
                  : undefined
            }
          >
            {importLeadsFromCsv.isLoading ? "Starting…" : "Run import"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default LeadImport;
